/**
 * Hit again › When (user, 18/10/2026: "hit again outside of the container
 * time — based on the window"): the new drop goes out the moment the rule
 * holds, any time the country window is open — after the container's last
 * drop too (the default, what hit again always did) — or only between the
 * container's first and last drop of the day. The line under the choice
 * says the real hours, Danish first. 20/10/2026 — a row of the hit-again
 * grid (action/HitGrid): "Goes out" in the label column.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Clock } from 'lucide-react';
import type { DropGenHitWhen } from '@trafficpulse/shared';

import type { ContainerDraft, DropGenPlanDetail } from '../../../../drop-generator.types';
import { rangeText, windowText } from '../../../../utils/clock/dk-label';
import { useCountryClock } from '../../../shared/time/country-clock-context';
import { ChoiceChips } from '../../general/controls/ChoiceChips';
import { useGeneralTimes } from '../../general/hooks/use-general-times';
import { HitRow } from './action/HitGrid';

const OPTIONS = [
  { value: 'WINDOW', label: 'Any time the window is open' },
  { value: 'CONTAINER', label: "Only in this container's hours" },
] as const;

interface Props {
  when: DropGenHitWhen;
  draft: ContainerDraft;
  plan: DropGenPlanDetail;
  containerId: string | null;
  onWhen: (when: DropGenHitWhen) => void;
}

export const HitWhenRow: FC<Props> = ({ when, draft, plan, containerId, onWhen }) => {
  const clock = useCountryClock();
  const { slotMinutes } = useGeneralTimes(draft, plan, containerId);
  const first = slotMinutes[0];
  const last = slotMinutes[slotMinutes.length - 1];
  const hours = first === undefined || last === undefined ? 'its first to its last drop of the day' : rangeText(first, last, clock);
  const line =
    when === 'CONTAINER'
      ? `Only between ${hours}. A drop that hits after its last drop is not sent again.`
      : draft.sendOutsideWindow
        ? 'Right when it hits, at any hour — this container sends outside the window (Schedule › Outside the window).'
        : `Right when it hits — after this container's last drop too, while the window is open (${windowText(plan.windowStartMin, plan.windowEndMin, clock)}).`;
  return (
    <HitRow
      icon={Clock}
      label="Goes out"
      testId="hit-when"
      note={
        <span key={when} className="block duration-300 animate-in fade-in-0 motion-reduce:animate-none">
          {line}
        </span>
      }
    >
      <ChoiceChips<DropGenHitWhen> options={OPTIONS} value={when} onChange={onWhen} ariaLabel="When the hit-again drop may go out" />
    </HitRow>
  );
};
